import { useState } from 'react';
import {
  Card,
  Stack,
  Text,
  Group,
  Badge,
  Box,
  Menu,
  ActionIcon,
  rem,
  Skeleton,
  Button,
  Checkbox,
} from "@mantine/core";
import { IconClock, IconUsers, IconDotsVertical, IconTrash, IconBookmark, IconBookmarkOff, IconMessageCircle, IconChartBar, IconCarrot, IconListCheck, IconPrinter, IconView360Arrow } from "@tabler/icons-react";
import { RecipeImage } from '../UI/RecipeImage';
import { useIsBottomSheet } from '../UI/ResponsiveDrawer';
import { Recipe } from "../../types/mealPlan";
import { useMediaQuery } from '@mantine/hooks';
import { getRecipeBadges, splitBadgesForDisplay } from '../../utils/recipeBadges';
import { MoreRestrictionsMenu } from '../UI/MoreRestrictionsMenu';
import { HouseholdSegment } from '../../utils/householdSegment';
import { displayFractions } from '../../utils/formatQuantity';
import { formatTime } from '../../utils/formatTime';
import { formatIngredientName, formatIngredientQuantity } from '../../utils/formatIngredient';
import { printRecipe } from '../../utils/printRecipe';
import { CuisineBadge } from '../UI/CuisineBadge';

interface QuickBiteRecipeCardProps {
  recipe: Recipe;
  onRecipeClick?: (recipe: Recipe) => void;
  onDelete?: (recipe: Recipe) => void;
  onToggleCookbook?: (recipe: Recipe) => void;
  onSendFeedback?: (recipe: Recipe) => void;
  onShowNutrition?: (recipe: Recipe) => void;
  maxTags?: number; // Max number of tags to show before "+x more"
  disableHover?: boolean; // Disable pointer cursor on image/title (for selection mode)
  isSelected?: boolean; // Whether the card is selected (for selection mode)
  userDietaryRestrictions?: string[] | null;
  // Household framing for the viewer (householdSegment util); gates
  // family-only tags like "kid-friendly". Omit when unknown.
  userHouseholdSegment?: HouseholdSegment;
  radius?: 'sm' | 'md' | 'lg' | 'xl'; // Card border radius
}

export const QuickBiteRecipeCard: React.FC<QuickBiteRecipeCardProps> = ({ recipe, onRecipeClick, onDelete, onToggleCookbook, onSendFeedback, onShowNutrition, maxTags = 3, disableHover = false, isSelected = false, userDietaryRestrictions, userHouseholdSegment, radius }) => {
  // Rendered inside QuickBiteDrawer; menus must not body-portal in a sheet
  const isBottomSheet = useIsBottomSheet();
  const [isFlipped, setIsFlipped] = useState(false);

  const handleClick = () => {
    if (onRecipeClick) {
      onRecipeClick(recipe);
    }
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClick();
  };

  // The card only turns over from the dedicated flip control, matching the
  // meal plan cards. A stray tap on the image or body never flips it.
  const handleFlipToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFlipped((prev) => !prev);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(recipe);
    }
  };

  const handleToggleCookbook = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleCookbook) {
      onToggleCookbook(recipe);
    }
  };

  const handleSendFeedback = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSendFeedback) {
      onSendFeedback(recipe);
    }
  };

  const handlePrint = (e: React.MouseEvent) => {
    e.stopPropagation();
    printRecipe(recipe);
  };

  const handleShowNutrition = () => {
    if (onShowNutrition) {
      onShowNutrition(recipe);
    }
  };

  const handleSelectClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClick();
  };

  const isMobile = useMediaQuery('(max-width: 768px)');

  // Back side content
  const maxIngredients = isMobile ? 6 : 12;
  const maxInstructions = isMobile ? 4 : 6;
  const ingredients = recipe.ingredients || [];
  const instructions = recipe.instructions || [];
  const moreIngredients = ingredients.length > maxIngredients ? ingredients.length - maxIngredients : 0;
  const moreInstructions = instructions.length > maxInstructions ? instructions.length - maxInstructions : 0;

  const ingredientsSection = (
    <>
      <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
        Ingredients
      </Text>
      <Stack gap={1}>
        {ingredients.slice(0, maxIngredients).map((ingredient, index) => (
          <Text key={index} size="xs" lineClamp={1}>
            <Text component="span" fw={600} size="xs">
              {formatIngredientQuantity(ingredient)}
            </Text>
            {' '}
            {formatIngredientName(ingredient)}
          </Text>
        ))}
        {moreIngredients > 0 && (
          <Text size="xs" c="dimmed" fs="italic">
            +{moreIngredients} more
          </Text>
        )}
      </Stack>
    </>
  );

  const instructionsSection = (
    <>
      <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb={4}>
        Instructions
      </Text>
      <Stack gap={4}>
        {instructions.slice(0, maxInstructions).map((instruction, index) => (
          <Group key={index} gap={6} align="flex-start" wrap="nowrap">
            <Text size="xs" fw={700} c="blue" style={{ flexShrink: 0, minWidth: 16 }}>
              {index + 1}.
            </Text>
            <Text size="xs" lineClamp={isMobile ? 2 : 4} style={{ flex: 1 }}>
              {displayFractions(instruction.text)}
            </Text>
          </Group>
        ))}
        {moreInstructions > 0 && (
          <Text size="xs" c="dimmed" fs="italic">
            +{moreInstructions} more step{moreInstructions > 1 ? 's' : ''}
          </Text>
        )}
      </Stack>
    </>
  );

  const backSideContent = (
    <>
      {/* Header with recipe name */}
      <Text fw={600} size="sm" lineClamp={1} mb="xs" ta="center">
        {recipe.name}
      </Text>

      {/* Content area - side by side on desktop, stacked on mobile */}
      <Box style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
        {isMobile ? (
          <Stack gap="sm">
            <Box>{ingredientsSection}</Box>
            <Box>{instructionsSection}</Box>
          </Stack>
        ) : (
          <Box style={{ display: 'flex', gap: 16 }}>
            <Box style={{ flex: '0 0 35%', minWidth: 0 }}>
              {ingredientsSection}
            </Box>
            <Box style={{ flex: '1 1 65%', minWidth: 0 }}>
              {instructionsSection}
            </Box>
          </Box>
        )}
      </Box>

      {/* Primary action + flip back. Tapping anywhere flips the card back, but
          that is not discoverable, so pair an explicit control with the
          primary action. */}
      <Group
        gap="xs"
        wrap="nowrap"
        onClick={(e) => e.stopPropagation()}
        style={{ pointerEvents: 'auto', position: 'relative', zIndex: 10, marginTop: 'var(--mantine-spacing-xs)' }}
      >
        <Box style={{ flex: 1, minWidth: 0 }}>
          {disableHover ? (
            <Button
              variant={isSelected ? "light" : "filled"}
              color={isSelected ? "green" : undefined}
              size="sm"
              fullWidth
              style={{ cursor: 'pointer' }}
              onClick={handleSelectClick}
            >
              {isSelected ? "Selected" : "Select"}
            </Button>
          ) : (
            <Button
              variant="light"
              size="sm"
              fullWidth
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleClick();
              }}
            >
              View full recipe
            </Button>
          )}
        </Box>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label="Back to recipe card"
          style={{ flexShrink: 0, pointerEvents: 'auto' }}
          onClick={handleFlipToggle}
        >
          <IconView360Arrow size={20} />
        </ActionIcon>
      </Group>
    </>
  );

  const handleCardClick = (e: React.MouseEvent) => {
    // In selection mode, clicking anywhere on the card picks the recipe, which
    // is what the checkbox and Select button already do
    if (disableHover) {
      e.stopPropagation();
      handleClick();
    }
  };

  const frontCard = (
    <Card withBorder h="100%" radius={radius} style={{
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      cursor: disableHover ? 'pointer' : undefined,
      backfaceVisibility: 'hidden',
      WebkitBackfaceVisibility: 'hidden',
      transform: 'rotateY(0deg)',
      WebkitTransform: 'rotateY(0deg)',
      // Safari fallback: explicitly hide when flipped since backface-visibility can be unreliable
      visibility: isFlipped ? 'hidden' : 'visible',
      transition: isFlipped ? 'visibility 0s 0.4s' : 'visibility 0s 0s',
      WebkitTransition: isFlipped ? 'visibility 0s 0.4s' : 'visibility 0s 0s',
      pointerEvents: isFlipped ? 'none' : undefined,
    }} onClick={handleCardClick}>
      {/* Selection checkbox in selection mode */}
      {disableHover && (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            handleClick();
          }}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 5,
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => {}}
            style={{ pointerEvents: 'none' }}
          />
        </Box>
      )}
      <Stack gap="md" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Recipe Image */}
        <Box
          style={{
            cursor: 'pointer',
          }}
          onClick={disableHover ? undefined : handleImageClick}
        >
          <RecipeImage
            src={recipe.imageUrl}
            alt={recipe.name}
            height={200}
            radius="md"
            fallbackIconSize={64}
          />
        </Box>

        {/* Recipe Info */}
        <Stack gap="sm">
          <Box style={{ position: 'relative' }}>
            <Text
              fw={600}
              size="xl"
              style={{
                cursor: disableHover ? 'pointer' : (onRecipeClick ? 'pointer' : 'default'),
                width: 'fit-content',
                marginRight: onDelete ? 40 : 0,
              }}
              onClick={disableHover ? undefined : handleClick}
            >
              {recipe.name}
            </Text>

            {/* Context Menu - Only show if onDelete is provided */}
            {onDelete && (
              <Group gap="xs" style={{ position: 'absolute', top: 0, right: 0 }}>
                <Menu shadow="md" width={220} position="bottom-end" withinPortal={!isBottomSheet}>
                  <Menu.Target>
                    <ActionIcon variant="subtle" size="lg">
                      <IconDotsVertical size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {onToggleCookbook && (
                      <>
                        {recipe.isSaved ? (
                          <Menu.Item
                            leftSection={<IconBookmarkOff style={{ width: rem(14), height: rem(14) }} />}
                            onClick={handleToggleCookbook}
                            color="orange"
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Remove from Cookbook
                          </Menu.Item>
                        ) : (
                          <Menu.Item
                            leftSection={<IconBookmark style={{ width: rem(14), height: rem(14) }} />}
                            onClick={handleToggleCookbook}
                            color="green"
                            style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                          >
                            Add to Cookbook
                          </Menu.Item>
                        )}
                        <Menu.Divider />
                      </>
                    )}
                    {onShowNutrition && recipe.nutrition && (
                      <Menu.Item
                        leftSection={<IconChartBar style={{ width: rem(14), height: rem(14) }} />}
                        onClick={handleShowNutrition}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        Nutrition Facts
                      </Menu.Item>
                    )}
                    {onSendFeedback && (
                      <Menu.Item
                        leftSection={<IconMessageCircle style={{ width: rem(14), height: rem(14) }} />}
                        onClick={handleSendFeedback}
                        style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                      >
                        Send feedback
                      </Menu.Item>
                    )}
                    <Menu.Item
                      leftSection={<IconPrinter style={{ width: rem(14), height: rem(14) }} />}
                      onClick={handlePrint}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Print recipe
                    </Menu.Item>
                    <Menu.Item
                      color="red"
                      leftSection={<IconTrash style={{ width: rem(14), height: rem(14) }} />}
                      onClick={handleDelete}
                      style={isMobile ? { fontSize: '16px', padding: '12px 16px' } : undefined}
                    >
                      Delete recipe
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Box>

          {recipe.description && (
            <Text size="sm" c="dimmed" lineClamp={2}>
              {recipe.description}
            </Text>
          )}

          {/* Recipe Details - Time, Servings, Difficulty */}
          <Group gap="xs">
            <Group gap="xs">
              <IconClock size={16} />
              <Text size="sm">{formatTime(recipe.totalTimeMinutes)}</Text>
            </Group>
            <Group gap="xs">
              <IconUsers size={16} />
              <Text size="sm">{recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}</Text>
            </Group>
            {recipe.difficulty && (
              <Badge
                size="sm"
                variant="light"
                color={recipe.difficulty === 'easy' ? 'green' : recipe.difficulty === 'medium' ? 'yellow' : 'red'}
              >
                {recipe.difficulty}
              </Badge>
            )}
          </Group>

          {/* Ingredients and Steps Count */}
          <Group gap="xs">
            <Group gap="xs">
              <IconCarrot size={16} />
              <Text size="sm">{recipe.ingredients?.length || 0} ingredients</Text>
            </Group>
            <Group gap="xs">
              <IconListCheck size={16} />
              <Text size="sm">{recipe.instructions?.length || 0} steps</Text>
            </Group>
          </Group>

          {/* Cuisine and Tags. This row can reach the card's bottom edge, so it
              reserves room for the floating flip control (34px icon + 11px offset). */}
          <Group gap="xs" pr={!disableHover ? 45 : undefined}>
            {recipe.cuisine && (
              <CuisineBadge cuisine={recipe.cuisine} />
            )}
            {(() => {
              const dietaryRestrictions = recipe.dietaryRestrictions || [];
              const tags = recipe.tags || [];
              const totalBadges = dietaryRestrictions.length + tags.length;

              // Show skeleton badges while user preferences are loading
              if (userDietaryRestrictions === null && totalBadges > 0) {
                const visibleCount = Math.min(totalBadges, maxTags);
                // Only overflowing restrictions get a "+N more", so approximate with those
                const hasMore = dietaryRestrictions.length > maxTags;
                // Vary widths to approximate actual badge sizes
                const widths = [80, 90, 70];

                return (
                  <>
                    {Array.from({ length: visibleCount }).map((_, i) => (
                      <Skeleton key={`skeleton-${i}`} height={18} width={widths[i % widths.length] || 80} radius="xl" />
                    ))}
                    {hasMore && (
                      <Skeleton height={18} width={60} radius="xl" />
                    )}
                  </>
                );
              }

              // Don't render anything if still loading with no badges
              if (userDietaryRestrictions === null || userDietaryRestrictions === undefined) {
                return null;
              }

              const allBadges = getRecipeBadges(recipe, {
                dietaryRestrictions: userDietaryRestrictions,
                householdSegment: userHouseholdSegment,
              });
              const { visible, hiddenRestrictions } = splitBadgesForDisplay(allBadges, maxTags);

              return (
                <>
                  {visible.map((badge) => (
                    <Badge key={`${badge.type}-${badge.text}`} size="sm" variant="outline">
                      {badge.text}
                    </Badge>
                  ))}
                  <MoreRestrictionsMenu restrictions={hiddenRestrictions} />
                </>
              );
            })()}
          </Group>
        </Stack>

        {/* Select button in selection mode, sharing its row with the flip
            control since the button owns the card's bottom edge there */}
        {disableHover && (
          <Group gap="xs" wrap="nowrap" mt="auto">
            <Button
              variant={isSelected ? "light" : "filled"}
              color={isSelected ? "green" : undefined}
              size="sm"
              style={{ flex: 1, minWidth: 0 }}
              onClick={handleSelectClick}
            >
              {isSelected ? "Selected" : "Select"}
            </Button>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label="See ingredients and steps"
              style={{ flexShrink: 0 }}
              onClick={handleFlipToggle}
            >
              <IconView360Arrow size={20} />
            </ActionIcon>
          </Group>
        )}
      </Stack>

      {/* Bottom-right flip control, floating over the card's corner the way the
          meal plan cards do. Selection mode gets its own copy in the button row. */}
      {!disableHover && (
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          aria-label="See ingredients and steps"
          style={{ position: 'absolute', bottom: 7, right: 11, zIndex: 3 }}
          onClick={handleFlipToggle}
        >
          <IconView360Arrow size={20} />
        </ActionIcon>
      )}
    </Card>
  );

  // Wrap in 3D flip container
  return (
    <Box
      style={{
        perspective: '1000px',
        WebkitPerspective: '1000px',
        height: '100%',
      }}
    >
      <Box
        style={{
          position: 'relative',
          height: '100%',
          transformStyle: 'preserve-3d',
          WebkitTransformStyle: 'preserve-3d',
          transition: 'transform 0.8s ease',
          WebkitTransition: 'transform 0.8s ease',
          transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        {/* Front side */}
        {frontCard}

        {/* Back side */}
        <Card
          withBorder
          h="100%"
          radius={radius}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            WebkitTransform: 'rotateY(180deg)',
            // Safari fallback: explicitly hide when not flipped since backface-visibility can be unreliable
            visibility: isFlipped ? 'visible' : 'hidden',
            transition: isFlipped ? 'visibility 0s 0s' : 'visibility 0s 0.4s',
            WebkitTransition: isFlipped ? 'visibility 0s 0s' : 'visibility 0s 0.4s',
            cursor: 'pointer',
            pointerEvents: isFlipped ? 'auto' : 'none',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setIsFlipped(false);
          }}
        >
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {backSideContent}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};
